import { SHIP } from "@tiny-seas/shared";

/** Where the free hand holds the bucket: not at all, on the rim's side, or under the base. */
export type BucketSupport = "free" | "rim" | "base";

/**
 * Held-bucket pose in the body frame (feet origin, +z forward, +x right). The bucket's
 * origin is its bail-handle grip (tools/models/bucket.py); the right hand always holds it.
 */
export interface BucketPose {
  x: number;
  y: number;
  z: number;
  yaw: number;
  /** Positive tips the mouth forward (pours over the front). */
  pitch: number;
  roll: number;
  /** Hip drop (m) and forward torso lean (rad) for the scoop and the throw. */
  hipDrop: number;
  lean: number;
  support: BucketSupport;
  /** Water drawn in the bucket. */
  water: boolean;
}

/** Bucket-local grip points for the free hand (bucket.py: rim at -0.15, base at -0.43). */
export const BUCKET_RIM_GRIP = { x: -0.16, y: -0.2, z: 0 } as const;
export const BUCKET_BASE_GRIP = { x: 0, y: -0.47, z: -0.03 } as const;
/** Throw progress at which the water leaves the bucket. */
export const BUCKET_RELEASE = SHIP.bucketThrowRelease / SHIP.bucketThrowSeconds;

interface Key {
  at: number;
  x: number; y: number; z: number;
  yaw: number; pitch: number; roll: number;
  hipDrop: number; lean: number;
}

/** Hanging at the right side, handle fore-and-aft, clear of the thigh. */
const CARRY: Key = { at: 0, x: 0.34, y: 0.80, z: 0.04, yaw: Math.PI / 2, pitch: 0, roll: 0, hipDrop: 0, lean: 0 };
const key = (at: number, x: number, y: number, z: number, yaw: number, pitch: number, hipDrop: number, lean: number): Key =>
  ({ at, x, y, z, yaw, pitch, roll: 0, hipDrop, lean });

/** Bend down, dip the bucket mouth-first into the deck water, scoop it upright and lift it back. */
const FILL: readonly Key[] = [
  CARRY,
  key(0.22, 0.14, 0.48, 0.42, 0.35, 0.95, 0.22, 0.65),
  key(0.50, 0.10, 0.42, 0.46, 0.15, 0.45, 0.24, 0.70),
  key(0.74, 0.14, 0.62, 0.40, 0.10, 0, 0.16, 0.45),
  { ...CARRY, at: 1 },
];
/** Wind the bucket back low, swing it up over the rail tipping the mouth forward, recover. */
const THROW: readonly Key[] = [
  CARRY,
  key(0.30, 0.20, 0.62, -0.05, 0, -0.5, 0.10, -0.05),
  key(BUCKET_RELEASE, 0.05, 1.42, 0.80, 0, 1.75, 0, 0.32),
  key(0.72, 0.06, 1.28, 0.74, 0, 2.2, 0, 0.25),
  { ...CARRY, at: 1 },
];
/** First person keeps the scoop within the arms' reach from the upright camera, in view below it. */
const FIRST_PERSON_FILL_LIFT = 0.4;
const FIRST_PERSON_FILL_REACH = 0.12;

const smooth = (t: number): number => t * t * (3 - 2 * t);

/**
 * Samples the held-bucket pose. `action` is the character action ("fill", "throw" or
 * anything else for the carry), `progress` its 0…1 progress, `stride` the walk phase.
 */
export function sampleBucketPoseToRef(action: string, progress: number, full: boolean, firstPerson: boolean, walking: boolean, stride: number, out: BucketPose): BucketPose {
  const keys = action === "fill" ? FILL : action === "throw" ? THROW : null;
  let a = CARRY, b = CARRY, t = 0;
  if (keys) {
    const p = Math.max(0, Math.min(1, progress));
    for (let i = 1; i < keys.length; i++) {
      if (p > keys[i]!.at && i < keys.length - 1) continue;
      a = keys[i - 1]!; b = keys[i]!;
      t = smooth(Math.max(0, Math.min(1, (p - a.at) / (b.at - a.at))));
      break;
    }
  }
  const lerp = (u: number, v: number): number => u + (v - u) * t;
  out.x = lerp(a.x, b.x); out.y = lerp(a.y, b.y); out.z = lerp(a.z, b.z);
  out.yaw = lerp(a.yaw, b.yaw); out.pitch = lerp(a.pitch, b.pitch); out.roll = lerp(a.roll, b.roll);
  out.hipDrop = lerp(a.hipDrop, b.hipDrop); out.lean = lerp(a.lean, b.lean);
  if (action === "fill" && firstPerson) {
    const scoop = Math.min(1, out.hipDrop / 0.2);
    out.y += FIRST_PERSON_FILL_LIFT * scoop;
    out.z += FIRST_PERSON_FILL_REACH * scoop;
  }
  if (!keys && walking) {
    // The hanging load swings against the stride.
    out.z += Math.sin(stride) * (full ? 0.03 : 0.05);
    out.roll = Math.sin(stride) * (full ? 0.05 : 0.09);
  }
  const p = Math.max(0, Math.min(1, progress));
  out.support = action === "fill" && p > 0.12 && p < 0.88 ? "rim" : action === "throw" && p > 0.1 && p < 0.85 ? "base" : "free";
  out.water = action === "fill" ? full || p >= 0.5 : action === "throw" ? full && p < BUCKET_RELEASE : full;
  return out;
}
